import { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

declare module 'axios' {
  interface AxiosRequestConfig {
    /** Retry this non-GET request on a network error too; set only on requests that are safe to repeat. */
    retryOnNetworkError?: boolean;
    /** How many network-error retries this request has already used. */
    networkRetryAttempt?: number;
  }
}

/** Short and few: long enough to ride out a cellular blip, short enough that a dead connection still fails fast. */
const DEFAULT_RETRY_DELAYS_MS = [300, 1000];

/**
 * A request that never got a response. On iOS WebKit (Safari and the Instagram/Facebook in-app browsers) this is
 * what a cellular hand-off or a backgrounded tab produces, and a moment later the same request goes through.
 */
export function isNetworkError(err: unknown): err is AxiosError {
  return err instanceof AxiosError && err.code === AxiosError.ERR_NETWORK && !err.response;
}

/** The page is being hidden or torn down, so a failed request has no shopper waiting on it. */
export function isPageHidden() {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

function isRetryable(config: InternalAxiosRequestConfig) {
  const method = (config.method ?? 'get').toLowerCase();
  return method === 'get' || method === 'head' || !!config.retryOnNetworkError;
}

/**
 * Resolves once the page is visible. A request that died while the shopper was in another app is retried when they
 * come back, so a dropped products or session call doesn't leave them on the reload screen.
 */
function whenVisible() {
  if (!isPageHidden()) {
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => {
    const onChange = () => {
      if (!isPageHidden()) {
        document.removeEventListener('visibilitychange', onChange);
        resolve();
      }
    };
    document.addEventListener('visibilitychange', onChange);
  });
}

interface NetworkRetryOptions {
  delaysMs?: number[];
  /** Called once per request that a retry rescued, with the attempt that got through. */
  onRecovered?: (config: InternalAxiosRequestConfig, attempts: number) => void;
}

/**
 * Retries idempotent requests that failed without a response. The server sees no error for these: one request drops
 * while the page's other calls succeed, so a retry a moment later usually gets through.
 */
export function installNetworkRetry(
  client: AxiosInstance,
  { delaysMs = DEFAULT_RETRY_DELAYS_MS, onRecovered }: NetworkRetryOptions = {},
) {
  client.interceptors.response.use(undefined, async (err) => {
    const config: InternalAxiosRequestConfig | undefined = err?.config;
    const attempt = config?.networkRetryAttempt ?? 0;
    if (!isNetworkError(err) || !config || !isRetryable(config) || attempt >= delaysMs.length) {
      throw err;
    }

    await whenVisible();
    await new Promise((resolve) => setTimeout(resolve, delaysMs[attempt]));
    const response = await client.request({ ...config, networkRetryAttempt: attempt + 1 });
    // nested retries each pass through here; report once, from the original request
    if (attempt === 0) {
      onRecovered?.(config, response.config.networkRetryAttempt ?? 1);
    }
    return response;
  });
}
